import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getStudentAttachment, getDownloadFileName } from '../utils/studentDocuments';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { StatusBadge, Progress, Avatar, Card, PageLoad, ErrorMsg } from '../components/ui';
import SetUpInterviewModal from '../components/SetUpInterviewModal';
import AvailabilityGrid, { isoToSlotKey, slotKeyToISO } from '../components/AvailabilityGrid';
import CampusLoginStudentSync from '../components/CampusLoginStudentSync';

const FULL_DOC_PROGRAMS = ['BMT', 'AMT', 'ATM', 'BRM', 'MOA'];
const DOC_FIELDS = [
  { key: 'doc_first_aid', label: 'First Aid Certificate', fullDocOnly: true },
  { key: 'doc_pic', label: 'Police Information Check', fullDocOnly: true },
  { key: 'doc_immunization', label: 'Immunization Record', fullDocOnly: true },
  { key: 'doc_resume', label: 'Resume', fullDocOnly: false },
];
const COMPLETION_FIELDS = [
  { key: 'timesheet_uploaded', label: 'Timesheet Uploaded' },
  { key: 'final_eval_uploaded', label: 'Practicum Evaluation Uploaded' },
  { key: 'exit_survey_submitted', label: 'Exit Survey Submitted' },
];

const STUDENT_EMAIL_TEMPLATES = [
  { key: 'doc_reminder',       label: 'Document Reminder',             triggerDesc: 'Auto: daily when pre-req docs are still missing' },
  { key: 'ready_to_launch_60', label: 'Book Meeting (2 Months Out)',   triggerDesc: 'Auto: 60 days before practicum start date' },
  { key: 'ready_to_launch_30', label: 'Final Countdown (30 Days Out)', triggerDesc: 'Auto: 30 days before practicum start date' },
  { key: 'goal_reached',       label: 'Congratulations — Hours Complete', triggerDesc: 'Auto: when all practicum hours are logged' },
];

const ATTACHMENT_SLOTS = [
  { type: 'resume',    label: 'Resume',              field: 'resume_path' },
  { type: 'outline',   label: 'Program Outline',     field: 'program_outline_path' },
  { type: 'portfolio', label: 'Practicum Portfolio', field: 'practicum_portfolio_path' },
];

const WORKFLOW_STEPS = [
  { key: 'outreach',    label: 'Host Outreach' },
  { key: 'scheduling',  label: 'Interview Scheduling' },
  { key: 'placement',   label: 'Placement Confirmed' },
];

function HeroStatusPill({ status }) {
  const LABELS = {
    prereq: 'Pre-Req', ready: 'Ready', host_pending_response: 'Outreach Sent',
    interview_scheduling: 'Scheduling', active: 'Active',
    completing: 'Completing', complete: 'Complete',
  };
  const COLORS = {
    active:    { bg: 'rgba(61,192,119,.14)',  color: '#7BE0A5', border: 'rgba(61,192,119,.30)',  dot: '#7BE0A5' },
    complete:  { bg: 'rgba(61,192,119,.14)',  color: '#7BE0A5', border: 'rgba(61,192,119,.30)',  dot: '#7BE0A5' },
    completing:{ bg: 'rgba(124,58,237,.14)',  color: '#C4B5FD', border: 'rgba(124,58,237,.30)',  dot: '#C4B5FD' },
    ready:     { bg: 'rgba(245,166,35,.14)',  color: '#FBC559', border: 'rgba(245,166,35,.30)',  dot: '#FBC559' },
    host_pending_response: { bg: 'rgba(91,160,229,.14)', color: '#93C5FD', border: 'rgba(91,160,229,.30)', dot: '#93C5FD' },
    interview_scheduling:  { bg: 'rgba(91,160,229,.14)', color: '#93C5FD', border: 'rgba(91,160,229,.30)', dot: '#93C5FD' },
    prereq:    { bg: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.70)', border: 'rgba(255,255,255,.16)', dot: 'rgba(255,255,255,.50)' },
  };
  const c = COLORS[status] || { bg: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.70)', border: 'rgba(255,255,255,.16)', dot: 'rgba(255,255,255,.50)' };
  return (
    <span className="status-pill" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      <span className="dot" style={{ background: c.dot }} />
      {LABELS[status] || status}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  // Parse date-only strings (YYYY-MM-DD) in local time to avoid UTC→local day shift
  const s = typeof d === 'string' ? d.split('T')[0] : d.toISOString().split('T')[0];
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
}
function fmtBookedSlotDisplay(iso, timezone) {
  if (!iso) return null;
  const d = new Date(iso);
  const opts = { year: 'numeric', month: 'short', day: 'numeric', timeZone: timezone || undefined };
  const timeOpts = { hour: '2-digit', minute: '2-digit', timeZone: timezone || undefined };
  return d.toLocaleDateString('en-CA', opts) + ' at ' + d.toLocaleTimeString('en-CA', timeOpts) +
    (timezone ? ` (${timezone})` : '');
}

export default function StudentDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [hours, setHours] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [hourForm, setHourForm] = useState({ week_ending_date: '', hours_submitted: '', supervisor_name: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [interviewSession, setInterviewSession] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [confirmDeleteHourId, setConfirmDeleteHourId] = useState(null);
  const [confirmDeleteStudent, setConfirmDeleteStudent] = useState(false);
  const [editingHourId, setEditingHourId] = useState(null);
  const [editHourForm, setEditHourForm] = useState({});
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sendingDirect, setSendingDirect] = useState(false);
  const [directError, setDirectError] = useState('');
  const [openThread, setOpenThread] = useState(null); // {baseSubject, messages[]}
  const [threadReplyBody, setThreadReplyBody] = useState('');
  const [sendingThreadReply, setSendingThreadReply] = useState(false);
  const [threadReplyError, setThreadReplyError] = useState('');
  const [previewModal, setPreviewModal] = useState(null); // {triggerType, label, subject, body}
  const [previewLoading, setPreviewLoading] = useState(null); // triggerType key while fetching
  const [previewSending, setPreviewSending] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [composeTab, setComposeTab] = useState('write'); // 'write' | 'templates'
  const [prereqUnlocked, setPrereqUnlocked] = useState(false);
  const [campusRequiredDocs, setCampusRequiredDocs] = useState(null);
  const composeRef = React.useRef(null);
  const receiveCampusRequiredDocs = useCallback(docs => setCampusRequiredDocs(docs), []);
  const receiveCampusStudent = useCallback(next => setStudent(prev => ({ ...prev, ...next })), []);
  useEffect(() => {
    if (loading || searchParams.get('workflow') !== 'host') return;
    document.getElementById('host-placement-workflow')?.scrollIntoView({ block: 'start' });
  }, [loading, searchParams]);

  const fetchData = async () => {
    try {
      const [studentRes, hoursRes, hostsRes, emailLogRes] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/students/${id}/hours`),
        api.get('/hosts'),
        api.get(`/students/${id}/email-log`),
      ]);
      setStudent(studentRes.data);
      setHours(hoursRes.data);
      setHosts(hostsRes.data);
      setEmailLog(emailLogRes.data);
      if (user.role === 'admin') {
        const usersRes = await api.get('/users');
        setUsers(usersRes.data);
      }
      if (['interview_scheduling', 'active'].includes(studentRes.data.status)) {
        try {
          const sessionRes = await api.get(`/students/${id}/interview-session`);
          setInterviewSession(sessionRes.data);
        } catch {
          setInterviewSession(null);
        }
      } else {
        setInterviewSession(null);
      }
    } catch {
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  // Live: while we're waiting on the student to pick an interview time, poll so the
  // workflow auto-updates to "Interview Confirmed" the moment they respond — no refresh.
  useEffect(() => {
    if (!student) return;
    const waiting = student.status === 'interview_scheduling';
    if (!waiting) return;

    const poll = async () => {
      try {
        const res = await api.get(`/students/${id}`);
        setStudent(res.data);
        if (['interview_scheduling', 'active'].includes(res.data.status)) {
          try {
            const s = await api.get(`/students/${id}/interview-session`);
            setInterviewSession(s.data);
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };

    const iv = setInterval(poll, 12000);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [student?.status, id]);

  const handleDocToggle = async (docKey, value) => {
    try {
      const res = await api.patch(`/students/${id}/docs`, { [docKey]: value });
      setStudent(prev => ({ ...prev, ...res.data }));
    } catch (err) { console.error(err); }
  };

  const handleCompletionToggle = async (field, value) => {
    try {
      const payload = { ...student, [field]: value };
      const res = await api.put(`/students/${id}`, payload);
      setStudent(res.data);
    } catch (err) { console.error(err); }
  };

  const handleLogHours = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/students/${id}/hours`, { ...hourForm, hours_submitted: parseFloat(hourForm.hours_submitted) });
      setHourForm({ week_ending_date: '', hours_submitted: '', supervisor_name: '', notes: '' });
      fetchData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to log hours'); }
  };

  const handleSaveHourEdit = async (logId) => {
    try {
      await api.put(`/students/${id}/hours/${logId}`, { ...editHourForm, hours_submitted: parseFloat(editHourForm.hours_submitted) });
      setEditingHourId(null);
      setEditHourForm({});
      fetchData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update hours entry'); }
  };

  const handleDeleteHour = async (logId) => {
    if (confirmDeleteHourId !== logId) { setConfirmDeleteHourId(logId); return; }
    setConfirmDeleteHourId(null);
    try {
      await api.delete(`/students/${id}/hours/${logId}`);
      fetchData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete entry'); }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/students/${id}`, editForm);
      setStudent(res.data);
      setEditing(false);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update student');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDeleteStudent) { setConfirmDeleteStudent(true); return; }
    try {
      await api.delete(`/students/${id}`);
      navigate('/students');
    } catch (err) {
      setError('Failed to delete student');
      setConfirmDeleteStudent(false);
    }
  };

  const handleUnarchive = async () => {
    try {
      const res = await api.patch(`/students/${id}/unarchive`);
      setStudent(prev => ({ ...prev, ...res.data }));
    } catch { setError('Failed to unarchive student'); }
  };

  const handleSendEmail = async (triggerType) => {
    setSendingEmail(triggerType);
    try {
      await api.post(`/students/${id}/send-email`, { trigger_type: triggerType });
      const logRes = await api.get(`/students/${id}/email-log`);
      setEmailLog(logRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally { setSendingEmail(null); }
  };

  const handleSendDirect = async (e) => {
    e.preventDefault();
    if (!composeSubject.trim() || !composeBody.trim()) return;
    setSendingDirect(true);
    setDirectError('');
    try {
      await api.post(`/students/${id}/send-direct`, { subject: composeSubject, body: composeBody });
      const logRes = await api.get(`/students/${id}/email-log`);
      setEmailLog(logRes.data);
      setComposeSubject('');
      setComposeBody('');
    } catch (err) {
      setDirectError(err.response?.data?.error || 'Failed to send email');
    } finally { setSendingDirect(false); }
  };

  const handlePreview = async (tmpl) => {
    setPreviewLoading(tmpl.key);
    setPreviewError('');
    try {
      const res = await api.get(`/students/${id}/email-preview/${tmpl.key}`);
      setPreviewModal({ triggerType: tmpl.key, label: tmpl.label, subject: res.data.subject, body: res.data.body, html: res.data.html, tab: 'preview' });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load email preview');
    } finally { setPreviewLoading(null); }
  };

  const handleSendPreview = async () => {
    if (!previewModal) return;
    setPreviewSending(true);
    setPreviewError('');
    try {
      await api.post(`/students/${id}/send-email`, {
        trigger_type: previewModal.triggerType,
        subject_override: previewModal.subject,
        body_override: previewModal.body,
      });
      const logRes = await api.get(`/students/${id}/email-log`);
      setEmailLog(logRes.data);
      setPreviewModal(null);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Failed to send email');
    } finally { setPreviewSending(false); }
  };

  const handleThreadReply = async (e, baseSubject) => {
    e.preventDefault();
    if (!threadReplyBody.trim()) return;
    setSendingThreadReply(true);
    setThreadReplyError('');
    try {
      const replySubject = 'Re: ' + baseSubject.replace(/^Re:\s*/i, '');
      await api.post(`/students/${id}/send-direct`, { subject: replySubject, body: threadReplyBody });
      const logRes = await api.get(`/students/${id}/email-log`);
      const newLog = logRes.data;
      setEmailLog(newLog);
      setThreadReplyBody('');
      // Rebuild the thread from updated log
      const normalizeSubj = s => (s || '').replace(/^(re|fwd):\s*/gi, '').trim();
      const key = baseSubject.toLowerCase();
      const updatedMessages = newLog
        .filter(e => normalizeSubj(e.subject || e.metadata?.subject || '').toLowerCase() === key)
        .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
      setOpenThread(prev => prev ? { ...prev, messages: updatedMessages } : null);
    } catch (err) {
      setThreadReplyError(err.response?.data?.error || 'Failed to send reply');
    } finally { setSendingThreadReply(false); }
  };

  if (loading) return <PageLoad />;
  if (!student) return <div className="page" style={{ color: 'var(--ink-3)' }}>Student not found</div>;

  const executedHosts = hosts.filter(h => h.agreement_status === 'executed');
  const requiredDocs = campusRequiredDocs || (FULL_DOC_PROGRAMS.includes(student.program_code)
    ? DOC_FIELDS : DOC_FIELDS.filter(d => !d.fullDocOnly));

  const initials = `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`.toUpperCase();

  const showWorkflow = ['ready', 'host_pending_response', 'interview_scheduling'].includes(student.status)
    || (student.status === 'active' && !!interviewSession?.booked_slot);

  const hoursLogged = parseFloat(student.hours_logged) || 0;
  const hoursRequired = student.hours_required || 150;
  const RING_CIRC = 452.39;
  const ringPct = Math.min(1, hoursLogged / hoursRequired);
  const ringOffset = RING_CIRC * (1 - ringPct);
  const firstActiveStageIdx = COMPLETION_FIELDS.findIndex(f => !student[f.key]);

  return (
    <div className="page">
      {error && <ErrorMsg>{error}</ErrorMsg>}

      {student.is_archived && (
        <div className="archived-banner">
          <div>
            <strong>Archived</strong> — This student record is excluded from active reports and lists.
          </div>
          {user.role === 'admin' && (
            <button className="btn btn-secondary btn-sm" onClick={handleUnarchive}>Unarchive</button>
          )}
        </div>
      )}

      {/* ─── PROFILE HERO ─── */}
      <section className="hero">
        <div className="hero-topo" aria-hidden="true">
          <svg viewBox="0 0 1200 240" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="sdp-dotg" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="12" cy="12" r="0.6" fill="rgba(255,255,255,0.10)" />
              </pattern>
            </defs>
            <rect width="1200" height="240" fill="url(#sdp-dotg)" />
            <g fill="none" stroke="rgba(245,166,35,0.30)" strokeWidth="1.4" strokeLinejoin="round">
              <path d="M-50 180 L 180 90 L 320 140 L 480 60 L 660 130 L 840 80 L 1020 140 L 1200 80" />
            </g>
            <g fill="none" stroke="rgba(245,166,35,0.16)" strokeWidth="1.1" strokeLinejoin="round">
              <path d="M-50 220 L 180 150 L 320 200 L 480 120 L 660 190 L 840 140 L 1020 200 L 1200 140" />
            </g>
            <circle cx="480" cy="60" r="14" fill="rgba(245,166,35,0.35)" />
            <circle cx="480" cy="60" r="3" fill="#FBC559" />
          </svg>
        </div>
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="hero-avatar">{initials}</div>
          <div className="hero-mid">
            <div className="hero-eyebrow">
              Student Record{student.campus_login_id ? ` · #${student.campus_login_id}` : ''}
            </div>
            <h1 className="hero-name">
              {student.first_name} {student.last_name}
              <HeroStatusPill status={student.status} />
            </h1>
            <div className="hero-meta">
              {student.email && (
                <a href={`mailto:${student.email}`}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 5 }}><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></svg>
                  {student.email}
                </a>
              )}
              {student.email && student.phone && <span className="sep" />}
              {student.phone && (
                <a href={`tel:${student.phone}`}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 5 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.72 13 13 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 13 13 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  {student.phone}
                </a>
              )}
              {student.campus_login_id && (student.email || student.phone) && <span className="sep" />}
              {student.campus_login_id && <span className="id">ID {student.campus_login_id}</span>}
            </div>
          </div>
          {user.role === 'admin' && !student.is_archived && (
            <div className="hero-actions">
              <button
                className="btn-hero btn-hero-edit"
                onClick={() => { setEditing(!editing); setEditForm(student); setError(''); }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
                {editing ? 'Cancel' : 'Edit'}
              </button>
              {confirmDeleteStudent ? (
                <>
                  <button className="btn-hero btn-hero-delete" onClick={handleDelete}>Confirm Delete?</button>
                  <button className="btn-hero btn-hero-edit" onClick={() => setConfirmDeleteStudent(false)}>Cancel</button>
                </>
              ) : (
                <button className="btn-hero btn-hero-delete" onClick={handleDelete}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ─── EDIT FORM ─── */}
      {editing && (
        <Card title="Edit Student Details" style={{ marginTop: 18, marginBottom: 0 }}>
          <div className="form-grid-3" style={{ gap: 14 }}>
            <EField label="First Name" value={editForm.first_name} onChange={v => setEditForm({ ...editForm, first_name: v })} />
            <EField label="Last Name" value={editForm.last_name} onChange={v => setEditForm({ ...editForm, last_name: v })} />
            <EField label="Email" value={editForm.email} onChange={v => setEditForm({ ...editForm, email: v })} />
            <EField label="Phone" value={editForm.phone || ''} onChange={v => setEditForm({ ...editForm, phone: v })} />
            <EField label="City" value={editForm.city || ''} onChange={v => setEditForm({ ...editForm, city: v })} />
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 }}>Province</label>
              <select className="select" value={editForm.province || ''} onChange={e => setEditForm({ ...editForm, province: e.target.value })}>
                <option value="">— Select province —</option>
                <option value="Alberta">Alberta</option>
                <option value="British Columbia">British Columbia</option>
                <option value="Manitoba">Manitoba</option>
                <option value="New Brunswick">New Brunswick</option>
                <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                <option value="Nova Scotia">Nova Scotia</option>
                <option value="Northwest Territories">Northwest Territories</option>
                <option value="Nunavut">Nunavut</option>
                <option value="Ontario">Ontario</option>
                <option value="Prince Edward Island">Prince Edward Island</option>
                <option value="Quebec">Quebec</option>
                <option value="Saskatchewan">Saskatchewan</option>
                <option value="Yukon">Yukon</option>
              </select>
            </div>
            <EField label="Campus Login ID" value={editForm.campus_login_id || ''} onChange={v => setEditForm({ ...editForm, campus_login_id: v })} />
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 }}>Host (executed only)</label>
              <select className="select" value={editForm.host_id || ''} onChange={e => setEditForm({ ...editForm, host_id: e.target.value ? parseInt(e.target.value) : null })}>
                <option value="">No Host</option>
                {executedHosts.map(h => <option key={h.id} value={h.id}>{h.org_name}</option>)}
              </select>
            </div>
            {user.role === 'admin' && (
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 }}>Assigned PC</label>
                <select className="select" value={editForm.assigned_pc_id || ''} onChange={e => setEditForm({ ...editForm, assigned_pc_id: e.target.value ? parseInt(e.target.value) : null })}>
                  <option value="">None</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                </select>
              </div>
            )}
            <EField label="Hours Required" type="number" value={editForm.hours_required || ''} onChange={v => setEditForm({ ...editForm, hours_required: parseInt(v) })} />
            <EField label="Cohort Start" type="date" value={editForm.cohort_start_date?.split('T')[0] || ''} onChange={v => setEditForm({ ...editForm, cohort_start_date: v })} />
            <EField label="Cohort End" type="date" value={editForm.cohort_end_date?.split('T')[0] || ''} onChange={v => setEditForm({ ...editForm, cohort_end_date: v })} />
            <EField label="Practicum Start" type="date" value={editForm.practicum_start_date?.split('T')[0] || ''} onChange={v => setEditForm({ ...editForm, practicum_start_date: v })} />
            <EField label="Practicum End" type="date" value={editForm.practicum_end_date?.split('T')[0] || ''} onChange={v => setEditForm({ ...editForm, practicum_end_date: v })} />
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => { setEditing(false); setError(''); }}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={handleSaveEdit}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </Card>
      )}

      {/* ─── INFO GRID ─── */}
      <section className="info-grid">
        {[
          { label: 'Program',        value: student.program_name || student.program_code || '—', muted: !(student.program_name || student.program_code) },
          { label: 'City',           value: student.city || '—',                                 muted: !student.city },
          { label: 'Province',       value: student.province || '—',                             muted: !student.province },
          { label: 'Host',           value: student.host_name || 'Not assigned',                 muted: !student.host_name },
          { label: 'Assigned PC',    value: student.pc_name || 'Not assigned',                   muted: !student.pc_name },
          { label: 'Cohort Start',   value: fmtDate(student.cohort_start_date),                  muted: !student.cohort_start_date },
          { label: 'Cohort End',     value: fmtDate(student.cohort_end_date),                    muted: !student.cohort_end_date },
          { label: 'Practicum Start',value: fmtDate(student.practicum_start_date),               muted: !student.practicum_start_date },
          { label: 'Practicum End',  value: fmtDate(student.practicum_end_date),                 muted: !student.practicum_end_date },
        ].map(cell => (
          <div key={cell.label} className="info-cell">
            <span className="info-label"><span className="pip" />{cell.label}</span>
            <span className={`info-val${cell.muted ? ' muted' : ''}`}>{cell.value}</span>
          </div>
        ))}
      </section>

      {/* ─── WORKFLOW PANEL ─── */}
      {showWorkflow && !student.is_archived && (
        <div id="host-placement-workflow" style={{ marginTop: 18 }}>
          {searchParams.get('workflow') === 'host' && (
            <div className="info-banner" style={{ marginBottom: 12 }}>
              Opened from a host email template. Review the placement or interview below; no email is sent until you confirm its workflow action.
            </div>
          )}
          <WorkflowPanel student={student} onUpdate={fetchData} user={user} bookedSession={interviewSession} emailLog={emailLog} />
        </div>
      )}

      {/* ─── DOCS + HOURS GRID ─── */}
      <div className="sdp-grid">

        {/* Pre-Req Documents */}
        <section className="sdp-card">
          {(() => {
            const done  = requiredDocs.filter(d => student[d.key]).length;
            const total = requiredDocs.length;
            const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
            const allDone = total > 0 && done === total;
            const locked  = allDone && !prereqUnlocked && !student.is_archived;
            return (
              <>
                <div className="card-head">
                  <div>
                    <span className="card-eyebrow">Compliance</span>
                    <h2 className="card-title">Pre-Req Documents</h2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {allDone && !student.is_archived && (
                      prereqUnlocked ? (
                        <button
                          onClick={() => setPrereqUnlocked(false)}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#127046', background: 'rgba(61,192,119,.12)', border: '1px solid rgba(61,192,119,.25)', borderRadius: 7, padding: '4px 10px', cursor: 'pointer' }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                          Lock
                        </button>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#127046', background: 'rgba(61,192,119,.10)', border: '1px solid rgba(61,192,119,.20)', borderRadius: 7, padding: '4px 10px' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-12"/></svg>
                          All collected
                        </span>
                      )
                    )}
                    <span className="card-foot-note">{done} / {total} received</span>
                  </div>
                </div>

                <div className="prereq-progress">
                  <div className="prereq-progress-text">
                    <strong>{pct}%</strong>
                    {pct === 100 ? 'complete · cleared for placement' : `complete · ${total - done} remaining`}
                  </div>
                  <div className="prereq-progress-track">
                    <div className="prereq-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                {/* Req list — with lock overlay when all docs are collected */}
                <div style={{ position: 'relative' }}>
                  <div className="req-list" style={{ opacity: locked ? 0.45 : 1, transition: 'opacity .2s', pointerEvents: locked ? 'none' : 'auto' }}>
                    {requiredDocs.map(doc => (
                      <div
                        key={doc.key}
                        className={`req-row${student[doc.key] ? ' done' : ''}`}
                        onClick={() => !student.is_archived && !locked && handleDocToggle(doc.key, !student[doc.key])}
                        style={{ cursor: (student.is_archived || locked) ? 'default' : 'pointer' }}
                      >
                        <span className="req-check">
                          {student[doc.key] && (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12l5 5 9-12" />
                            </svg>
                          )}
                        </span>
                        <span className="req-name">{doc.label}</span>
                        <span className="req-time">{student.is_archived ? 'Read-only' : student[doc.key] ? '✓' : '—'}</span>
                      </div>
                    ))}
                  </div>

                  {/* Lock overlay */}
                  {locked && (
                    <div style={{ position: 'absolute', inset: 0, borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'rgba(255,255,255,.72)', backdropFilter: 'blur(3px)', zIndex: 2 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(61,192,119,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#127046' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0B1F2E', marginBottom: 3 }}>Prerequisites have been collected</div>
                        <div style={{ fontSize: 12, color: '#5C7388' }}>All documents are on file for this student.</div>
                      </div>
                      <button
                        onClick={() => setPrereqUnlocked(true)}
                        style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#0B1F2E', background: '#fff', border: '1px solid var(--line-2)', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
                        Edit
                      </button>
                    </div>
                  )}
                </div>
                <CampusLoginStudentSync
                  studentId={id}
                  archived={!!student.is_archived}
                  onRequiredDocs={receiveCampusRequiredDocs}
                  onApplied={receiveCampusStudent}
                />
              </>
            );
          })()}
        </section>

        {/* Hours Progress */}
        <section className="sdp-card hours-card">
          <div className="card-head">
            <div>
              <span className="card-eyebrow">Practicum</span>
              <h2 className="card-title">Hours Progress</h2>
            </div>
            <span className="card-foot-note">{hoursLogged} / {hoursRequired} hrs</span>
          </div>
          <div className="hours-body">
            <div className="sdp-ring">
              <svg viewBox="0 0 170 170" aria-hidden="true" focusable="false">
                <defs>
                  <linearGradient id="sdp-ringGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FBC559" />
                    <stop offset="100%" stopColor="#C97A0E" />
                  </linearGradient>
                </defs>
                <circle className="sdp-ring-bg" cx="85" cy="85" r="72" />
                <circle
                  className="sdp-ring-fg"
                  cx="85" cy="85" r="72"
                  strokeDasharray="452.39"
                  strokeDashoffset={ringOffset}
                  stroke="url(#sdp-ringGrad)"
                />
              </svg>
              <div className="sdp-ring-center">
                <div className="sdp-ring-big">{hoursLogged}</div>
                <div className="sdp-ring-sub">of {hoursRequired} hrs</div>
              </div>
              <span className="sdp-ring-pct">{Math.round(ringPct * 100)}%</span>
            </div>
            <div className="stages">
              {COMPLETION_FIELDS.map((field, i) => (
                <div
                  key={field.key}
                  className={`stage${student[field.key] ? ' done' : (i === firstActiveStageIdx ? ' active' : '')}`}
                  onClick={() => !student.is_archived && handleCompletionToggle(field.key, !student[field.key])}
                  style={{ cursor: student.is_archived ? 'default' : 'pointer' }}
                >
                  <span className="stage-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="stage-name">{field.label}</span>
                  <span className="stage-when">{student[field.key] ? 'Done' : i === firstActiveStageIdx ? 'Next up' : '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>

      {/* ─── ATTACHMENTS ─── */}
      <AttachmentsCard student={student} onUpdate={fetchData} user={user} />

      {/* ─── EMAIL COMMUNICATIONS ─── */}
      <section className="sdp-card" style={{ marginTop: 18 }}>
        <div className="card-head">
          <div>
            <span className="card-eyebrow">Outreach</span>
            <h2 className="card-title">Email Communications</h2>
          </div>
        </div>
        {student.email && (
          <div className="comm-recipient">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></svg>
            <span><strong>Sent to</strong>{student.email}</span>
          </div>
        )}
        <div className="comm-list">
          {STUDENT_EMAIL_TEMPLATES.map(tmpl => {
            const count = emailLog.filter(e => e.trigger_type === tmpl.key).length;
            const isSending = sendingEmail === tmpl.key;
            const sent = count > 0;
            return (
              <div key={tmpl.key} className={`comm${sent ? ' sent' : ''}`}>
                <span className="comm-mark">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13M22 2L15 22L11 13L2 9z" />
                  </svg>
                </span>
                <div className="comm-body">
                  <div className="comm-name">{tmpl.label}</div>
                  <div className="comm-trigger">{tmpl.triggerDesc}</div>
                </div>
                <span className="comm-status">{sent ? `Sent ${count}×` : 'Never sent'}</span>
                {!student.is_archived && (
                  <button
                    className="comm-send"
                    disabled={!!previewLoading || !student.email}
                    onClick={() => handlePreview(tmpl)}
                  >
                    {previewLoading === tmpl.key ? 'Loading…' : (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                        Preview
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── EMAIL CONVERSATION THREAD ─── */}
      {(() => {
        const normalizeSubj = s => (s || '').replace(/^(re|fwd):\s*/gi, '').trim();

        // Group emails into threads by normalized base subject
        const threadsMap = {};
        for (const entry of emailLog) {
          const rawSubj = entry.subject || entry.metadata?.subject || '';
          const key = normalizeSubj(rawSubj).toLowerCase() || `__id_${entry.id}`;
          if (!threadsMap[key]) threadsMap[key] = { baseSubject: normalizeSubj(rawSubj) || '(no subject)', messages: [] };
          threadsMap[key].messages.push(entry);
          // Prefer a non-Re subject as the display label
          if (!rawSubj.match(/^re:/i) && normalizeSubj(rawSubj)) threadsMap[key].baseSubject = normalizeSubj(rawSubj);
        }
        const threads = Object.values(threadsMap)
          .map(t => ({ ...t, messages: [...t.messages].sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at)) }))
          .sort((a, b) => new Date(b.messages[b.messages.length - 1].sent_at) - new Date(a.messages[a.messages.length - 1].sent_at));

        const TEMPLATE_LABELS = {
          documents_required: 'Documents Required',
          doc_reminder: 'Document Reminder',
          ready_to_launch_60: 'Book Meeting (2 Months Out)',
          ready_to_launch_30: 'Final Countdown (30 Days Out)',
          weekly_check_in: 'Weekly Check-In',
          host_outreach: 'Host Outreach',
          host_scheduling: 'Host Scheduling',
          student_scheduling: 'Student Scheduling',
          placement_confirmed: 'Placement Confirmed',
          direct: 'Direct Message',
        };
        return (
          <>
            {/* ── Email Preview & Edit modal ── */}
            {previewModal && (() => {
              const isPreview = previewModal.tab === 'preview';
              const previewHtml = previewModal.html
                ? `<base href="${window.location.origin}">` + previewModal.html
                : '';
              return (
                <div onClick={() => { setPreviewModal(null); setPreviewError(''); }} style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,.60)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                  <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--line-2)', borderRadius: 14, width: '100%', maxWidth: 720, height: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,.45)' }}>

                    {/* ── Header ── */}
                    <div style={{ padding: '14px 18px 0', borderBottom: '1px solid var(--line-2)', display: 'flex', flexDirection: 'column', gap: 0, flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-1)', lineHeight: 1.2 }}>Email Preview</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{previewModal.label} → <strong style={{ color: 'var(--ink-2)' }}>{student.email}</strong></div>
                        </div>
                        <button onClick={() => { setPreviewModal(null); setPreviewError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4, display: 'flex', alignItems: 'center' }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                      {/* Tabs */}
                      <div style={{ display: 'flex', gap: 0 }}>
                        {['preview', 'edit'].map(t => (
                          <button key={t} onClick={() => setPreviewModal(m => ({ ...m, tab: t }))} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', borderBottom: previewModal.tab === t ? '2px solid #F5A623' : '2px solid transparent', color: previewModal.tab === t ? '#F5A623' : 'var(--ink-3)', textTransform: 'capitalize', marginBottom: -1, transition: 'color .15s' }}>
                            {t === 'preview' ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                Preview
                              </span>
                            ) : (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
                                Edit
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── Body ── */}
                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      {isPreview ? (
                        /* HTML email rendered in iframe */
                        <iframe
                          title="Email preview"
                          srcDoc={previewHtml}
                          sandbox="allow-same-origin"
                          style={{ flex: 1, width: '100%', border: 'none', background: '#f5f5f5' }}
                        />
                      ) : (
                        /* Edit mode */
                        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                          <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Subject</label>
                            <input
                              type="text"
                              value={previewModal.subject}
                              onChange={e => setPreviewModal(m => ({ ...m, subject: e.target.value }))}
                              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--line-2)', background: 'var(--bg-page)', color: 'var(--ink-1)', fontSize: 14, outline: 'none' }}
                            />
                          </div>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Message Body</label>
                            <textarea
                              value={previewModal.body}
                              onChange={e => setPreviewModal(m => ({ ...m, body: e.target.value }))}
                              style={{ flex: 1, width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--line-2)', background: 'var(--bg-page)', color: 'var(--ink-1)', fontSize: 13, lineHeight: 1.65, resize: 'none', outline: 'none', fontFamily: 'inherit', minHeight: 340 }}
                            />
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Changes to the message body here are sent as plain text. The styled email layout is applied automatically.</div>
                          {previewError && <div style={{ fontSize: 13, color: '#F87171', background: 'rgba(248,113,113,.1)', borderRadius: 8, padding: '8px 12px' }}>{previewError}</div>}
                        </div>
                      )}
                    </div>

                    {/* ── Footer ── */}
                    <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {isPreview ? 'Switch to Edit to modify the subject or message before sending.' : 'Preview shows the final styled email the student will receive.'}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => { setPreviewModal(null); setPreviewError(''); }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--line-2)', background: 'none', color: 'var(--ink-2)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                        <button
                          onClick={handleSendPreview}
                          disabled={previewSending || !previewModal.subject.trim() || !previewModal.body.trim()}
                          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: previewSending ? 'var(--line-2)' : '#3DC077', color: '#fff', fontSize: 13, fontWeight: 600, cursor: previewSending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}
                        >
                          {previewSending ? 'Sending…' : (
                            <>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2L15 22L11 13L2 9z"/></svg>
                              Send Email
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })()}

            {/* ── Thread viewer modal ── */}
            {openThread && (
              <div onClick={() => setOpenThread(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--line-2)', borderRadius: 14, width: '100%', maxWidth: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,.35)' }}>
                  {/* Header */}
                  <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-1)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{openThread.baseSubject}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{openThread.messages.length} message{openThread.messages.length !== 1 ? 's' : ''}</div>
                    </div>
                    <button onClick={() => setOpenThread(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4, borderRadius: 6, flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>

                  {/* Messages */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {openThread.messages.map((msg, i) => {
                      const isOut = msg.direction !== 'inbound';
                      return (
                        <div key={i} style={{ background: isOut ? 'rgba(91,160,229,.06)' : 'rgba(34,197,94,.05)', border: `1px solid ${isOut ? 'rgba(91,160,229,.18)' : 'rgba(34,197,94,.18)'}`, borderRadius: 10, padding: '11px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isOut ? 'rgba(91,160,229,.15)' : 'rgba(34,197,94,.12)', color: isOut ? '#60A5FA' : '#22C55E' }}>
                              {isOut
                                ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2L15 22L11 13L2 9z" /></svg>
                                : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7" /><line x1="20" y1="12" x2="4" y2="12" /></svg>
                              }
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>
                                {isOut ? 'MCG Career Services' : (msg.metadata?.fromName || msg.from_address || 'Student')}
                              </span>
                              {isOut && msg.recipient && <span style={{ fontSize: 11, color: 'var(--ink-4)', marginLeft: 6 }}>→ {msg.recipient}</span>}
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--ink-4)', flexShrink: 0 }}>{new Date(msg.sent_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                          </div>
                          {msg.metadata?.body_text
                            ? <div style={{ fontSize: 13, color: 'var(--ink-1)', whiteSpace: 'pre-wrap', lineHeight: 1.65, fontFamily: 'inherit' }}>{msg.metadata.body_text}</div>
                            : <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>No body preview available.</div>
                          }
                        </div>
                      );
                    })}
                  </div>

                  {/* Inline reply */}
                  <div style={{ borderTop: '1px solid var(--line-2)', padding: '12px 20px', flexShrink: 0 }}>
                    {!student.is_archived && student.email ? (
                      <form onSubmit={e => handleThreadReply(e, openThread.baseSubject)}>
                        <textarea
                          className="log-input"
                          style={{ width: '100%', minHeight: 76, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 13 }}
                          placeholder={`Reply to this thread…`}
                          value={threadReplyBody}
                          onChange={e => setThreadReplyBody(e.target.value)}
                        />
                        {threadReplyError && <div style={{ color: '#DC2626', fontSize: 12, marginTop: 4 }}>{threadReplyError}</div>}
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={sendingThreadReply || !threadReplyBody.trim()}>
                            {sendingThreadReply ? 'Sending…' : (
                              <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}><path d="M22 2L11 13M22 2L15 22L11 13L2 9z" /></svg>Send Reply</>
                            )}
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpenThread(null)}>Close</button>
                        </div>
                      </form>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenThread(null)}>Close</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <section className="sdp-card" style={{ marginTop: 18 }}>
              <div className="card-head">
                <div>
                  <span className="card-eyebrow">Inbox &amp; Outbox</span>
                  <h2 className="card-title">Email Conversation</h2>
                </div>
                <span className="card-foot-note">inbox checked every 1 min</span>
              </div>

              {/* Thread list — one row per conversation */}
              {threads.length === 0 ? (
                <div className="replies-empty">
                  <div className="replies-empty-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" /></svg>
                  </div>
                  <div className="replies-empty-title">No emails yet</div>
                  <div className="replies-empty-sub">Sent emails and replies from {student.first_name} will appear here.</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {threads.map((thread, i) => {
                    const last = thread.messages[thread.messages.length - 1];
                    const isLastOut = last.direction !== 'inbound';
                    const hasInbound = thread.messages.some(m => m.direction === 'inbound');
                    const msgCount = thread.messages.length;
                    const preview = last.metadata?.body_text?.slice(0, 72) || '';
                    return (
                      <div
                        key={i}
                        onClick={() => { setOpenThread(thread); setThreadReplyBody(''); setThreadReplyError(''); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', transition: 'background .12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-sunken)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Icon — reflects last message direction; badge = message count */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLastOut ? 'rgba(91,160,229,.15)' : 'rgba(34,197,94,.12)', color: isLastOut ? '#60A5FA' : '#22C55E' }}>
                            {isLastOut
                              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2L15 22L11 13L2 9z" /></svg>
                              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7" /><line x1="20" y1="12" x2="4" y2="12" /></svg>
                            }
                          </div>
                          {msgCount > 1 && (
                            <div style={{ position: 'absolute', top: -3, right: -3, minWidth: 15, height: 15, borderRadius: 99, background: 'var(--amber)', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: '1.5px solid var(--bg-card)' }}>
                              {msgCount}
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '65%' }}>
                              {thread.baseSubject}
                            </span>
                            {!isLastOut && hasInbound && (
                              <span style={{ fontSize: 10, background: '#22C55E', color: '#fff', borderRadius: 99, padding: '1px 6px', fontWeight: 700, flexShrink: 0, letterSpacing: '0.02em' }}>Re</span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {preview || (isLastOut ? 'Sent' : 'Received')}
                          </div>
                        </div>

                        {/* Date */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{fmtDate(last.sent_at)}</span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--ink-4)' }}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Send Email — tabbed compose ── */}
              {!student.is_archived && student.email && (
                <div ref={composeRef} style={{ marginTop: 20, borderTop: '1px solid var(--line-2)' }}>

                  {/* Tab bar */}
                  <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--line-2)', paddingTop: 4 }}>
                    {[
                      { key: 'write',     label: 'Write Email',
                        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg> },
                      { key: 'templates', label: 'Use a Template',
                        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
                    ].map(t => (
                      <button key={t.key} onClick={() => setComposeTab(t.key)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', borderBottom: composeTab === t.key ? '2px solid #F5A623' : '2px solid transparent', color: composeTab === t.key ? '#F5A623' : 'var(--ink-3)', marginBottom: -1, transition: 'color .15s', whiteSpace: 'nowrap' }}>
                        {t.icon}{t.label}
                      </button>
                    ))}
                  </div>

                  {/* ── Write tab ── */}
                  {composeTab === 'write' && (
                    <div style={{ paddingTop: 16 }}>
                      <form onSubmit={handleSendDirect}>
                        <input className="log-input" style={{ width: '100%', marginBottom: 8, boxSizing: 'border-box' }} placeholder="Subject" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} required />
                        <textarea className="log-input" style={{ width: '100%', minHeight: 110, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} placeholder={`Hi ${student.first_name},\n\n`} value={composeBody} onChange={e => setComposeBody(e.target.value)} required />
                        {directError && <div style={{ color: '#DC2626', fontSize: 12, marginTop: 6 }}>{directError}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={sendingDirect || !composeSubject.trim() || !composeBody.trim()}>
                            {sendingDirect ? 'Sending…' : (
                              <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}><path d="M22 2L11 13M22 2L15 22L11 13L2 9z" /></svg>Send to {student.email}</>
                            )}
                          </button>
                          {(composeSubject || composeBody) && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setComposeSubject(''); setComposeBody(''); setDirectError(''); }}>Clear</button>
                          )}
                        </div>
                      </form>
                    </div>
                  )}

                  {/* ── Templates tab ── */}
                  {composeTab === 'templates' && (
                    <div style={{ paddingTop: 16 }}>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
                        Choose a template — you'll be able to preview and edit it before it sends.
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                        {STUDENT_EMAIL_TEMPLATES.map(tmpl => {
                          const count = emailLog.filter(e => e.trigger_type === tmpl.key).length;
                          const sent = count > 0;
                          const isLoading = previewLoading === tmpl.key;
                          return (
                            <button
                              key={tmpl.key}
                              onClick={() => handlePreview(tmpl)}
                              disabled={!!previewLoading}
                              style={{ textAlign: 'left', background: sent ? 'rgba(61,192,119,.04)' : 'var(--bg-page)', border: `1px solid ${sent ? 'rgba(61,192,119,.25)' : 'var(--line-2)'}`, borderRadius: 10, padding: '13px 14px', cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s', display: 'flex', flexDirection: 'column', gap: 6, opacity: !!previewLoading && !isLoading ? 0.5 : 1 }}
                              onMouseEnter={e => { if (!previewLoading) { e.currentTarget.style.borderColor = '#F5A623'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245,166,35,.12)'; } }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = sent ? 'rgba(61,192,119,.25)' : 'var(--line-2)'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                              {/* Name row */}
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink-1)', lineHeight: 1.3 }}>{tmpl.label}</span>
                                {sent && (
                                  <span style={{ fontSize: 10, background: 'rgba(61,192,119,.12)', color: '#127046', borderRadius: 99, padding: '2px 7px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>Sent {count}×</span>
                                )}
                              </div>
                              {/* Trigger desc */}
                              <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.45 }}>{tmpl.triggerDesc}</div>
                              {/* CTA */}
                              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: isLoading ? 'var(--ink-3)' : '#F5A623' }}>
                                {isLoading ? (
                                  <>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    Loading…
                                  </>
                                ) : (
                                  <>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                    Preview &amp; Send
                                  </>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>
              )}
            </section>
          </>
        );
      })()}

      {/* ─── HOURS LOG ─── */}
      <section className="sdp-card" style={{ marginTop: 18 }}>
        <div className="card-head">
          <div>
            <span className="card-eyebrow">Tracking</span>
            <h2 className="card-title">Hours Log</h2>
          </div>
          <span className="card-foot-note">weekly practicum hours</span>
        </div>

        {!student.is_archived && (
          <form onSubmit={handleLogHours} className="log-form">
            <div className="log-field">
              <label className="log-label">Week Ending</label>
              <input className="log-input" type="date" value={hourForm.week_ending_date}
                onChange={e => setHourForm({ ...hourForm, week_ending_date: e.target.value })} required />
            </div>
            <div className="log-field">
              <label className="log-label">Hours</label>
              <input className="log-input" type="number" step="0.25" value={hourForm.hours_submitted}
                onChange={e => setHourForm({ ...hourForm, hours_submitted: e.target.value })} placeholder="7.5" required />
            </div>
            <div className="log-field">
              <label className="log-label">Supervisor</label>
              <input className="log-input" value={hourForm.supervisor_name}
                onChange={e => setHourForm({ ...hourForm, supervisor_name: e.target.value })} placeholder="Name" />
            </div>
            <div className="log-field">
              <label className="log-label">Notes</label>
              <input className="log-input" value={hourForm.notes}
                onChange={e => setHourForm({ ...hourForm, notes: e.target.value })} placeholder="Optional" />
            </div>
            <button className="log-submit" type="submit">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
              Log Hours
            </button>
          </form>
        )}

        {student.is_archived && (
          <div style={{ padding: '12px 22px', fontSize: 12.5, color: 'var(--coral-2)', fontStyle: 'italic' }}>
            Hours log is read-only for archived students.
          </div>
        )}

        <div className="log-table">
          <div className="log-table-head">
            <span>Week Ending</span>
            <span>Hours</span>
            <span>Supervisor</span>
            <span>Notes</span>
            {!student.is_archived && <span />}
          </div>
          {hours.length === 0 && (
            <div className="log-empty">No hours logged yet — use the form above to add your first entry.</div>
          )}
          {hours.map(h => (
            editingHourId === h.id ? (
              <div key={h.id} className="log-row" style={{ background: 'var(--bg-sunken)' }}>
                <input type="date" className="log-input" value={editHourForm.week_ending_date}
                  onChange={e => setEditHourForm({ ...editHourForm, week_ending_date: e.target.value })}
                  style={{ fontSize: 12, padding: '4px 8px' }} />
                <input type="number" step="0.25" className="log-input" value={editHourForm.hours_submitted}
                  onChange={e => setEditHourForm({ ...editHourForm, hours_submitted: e.target.value })}
                  style={{ fontSize: 12, padding: '4px 8px' }} />
                <input className="log-input" value={editHourForm.supervisor_name}
                  onChange={e => setEditHourForm({ ...editHourForm, supervisor_name: e.target.value })}
                  style={{ fontSize: 12, padding: '4px 8px' }} />
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{editHourForm.notes || '—'}</span>
                <span className="actions">
                  <button className="btn btn-primary btn-xs" onClick={() => handleSaveHourEdit(h.id)}>Save</button>
                  <button className="btn btn-ghost btn-xs" onClick={() => { setEditingHourId(null); setEditHourForm({}); }}>Cancel</button>
                </span>
              </div>
            ) : (
              <div key={h.id} className="log-row">
                <span className="week">{h.week_ending_date?.split('T')[0] || '—'}</span>
                <span className="hours">{h.hours_submitted}h</span>
                <span>{h.supervisor_name || '—'}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.notes || '—'}</span>
                {!student.is_archived && (
                  <span className="actions">
                    <button className="log-action" title="Edit"
                      onClick={() => { setEditingHourId(h.id); setEditHourForm({ week_ending_date: h.week_ending_date?.split('T')[0] || '', hours_submitted: h.hours_submitted, supervisor_name: h.supervisor_name || '', notes: h.notes || '' }); }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
                    </button>
                    {confirmDeleteHourId === h.id ? (
                      <>
                        <button className="btn btn-xs" style={{ background: '#B91C1C', color: '#fff', padding: '4px 8px', borderRadius: 6, fontSize: 11 }} onClick={() => handleDeleteHour(h.id)}>Sure?</button>
                        <button className="log-action" onClick={() => setConfirmDeleteHourId(null)}>✕</button>
                      </>
                    ) : (
                      <button className="log-action danger" title="Delete" onClick={() => handleDeleteHour(h.id)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                    )}
                  </span>
                )}
              </div>
            )
          ))}
        </div>
      </section>

    </div>
  );
}

// ── Workflow Panel ────────────────────────────────────────────────────────────

function SchedLink({ label, url }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="sched-link-row">
      <span className="sched-link-label">{label}</span>
      <span className="sched-link-url">{url}</span>
      <button className="btn btn-ghost btn-xs" onClick={copy}>
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
}

function WorkflowPanel({ student, onUpdate, user, bookedSession, emailLog }) {
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [wError, setWError] = useState('');
  const [matchingHosts, setMatchingHosts] = useState(null);
  const [selectedHostId, setSelectedHostId] = useState('');
  const [showSetUpModal, setShowSetUpModal] = useState(false);
  const [sendingPlacement, setSendingPlacement] = useState(false);
  const [placementSentNow, setPlacementSentNow] = useState(false);
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);

  const hasAllFiles = ATTACHMENT_SLOTS.every(slot => getStudentAttachment(student, slot).stored);

  const stepIndex = student.status === 'active' ? 3
    : student.status === 'host_pending_response' ? 1
    : student.status === 'interview_scheduling' ? 2
    : 0;

  useEffect(() => {
    if (student.status === 'interview_scheduling') fetchSession();
  }, [student.status, student.id]); // eslint-disable-line

  const fetchSession = async () => {
    setSessionLoading(true);
    setWError('');
    try {
      const res = await api.get(`/students/${student.id}/interview-session`);
      setSession(res.data);
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to load scheduling session.');
    } finally { setSessionLoading(false); }
  };

  const handleFindHost = async () => {
    setBusy(true); setWError('');
    try {
      const res = await api.get(`/students/${student.id}/matching-hosts`);
      setMatchingHosts(res.data);
      const requestedHost = searchParams.get('host_id');
      if (requestedHost && res.data.some(h => String(h.id) === requestedHost)) {
        setSelectedHostId(requestedHost);
      }
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to find matching hosts');
    } finally { setBusy(false); }
  };

  const handleAssignHost = async () => {
    if (!selectedHostId) return;
    setBusy(true); setWError('');
    try {
      await api.post(`/students/${student.id}/assign-host`, { host_id: parseInt(selectedHostId) });
      setMatchingHosts(null);
      setSelectedHostId('');
      onUpdate();
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to assign host');
    } finally { setBusy(false); }
  };

  const handleBookSlot = async (key) => {
    if (!session) return;
    setBookingBusy(true); setWError('');
    try {
      const isoSlot = slotKeyToISO(key, session.timezone);
      await api.post(`/students/${student.id}/interview-session/book`, { slot: isoSlot });
      onUpdate();
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to book slot.');
    } finally { setBookingBusy(false); }
  };

  // New flow: coordinator books one of the offered slots on the student's behalf
  const handleBookOffered = async (iso) => {
    setBookingBusy(true); setWError('');
    try {
      await api.post(`/students/${student.id}/interview-session/book`, { slot: iso });
      onUpdate();
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to book slot.');
    } finally { setBookingBusy(false); }
  };

  const fmtOffered = (iso) => new Date(iso).toLocaleString('en-CA', {
    timeZone: session?.timezone || undefined, weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  const placementAlreadySent = placementSentNow
    || (emailLog || []).some(e => e.trigger_type === 'placement_confirmed');

  const handleSendPlacementConfirmed = async () => {
    setSendingPlacement(true);
    setWError('');
    try {
      await api.post(`/students/${student.id}/send-email`, { trigger_type: 'placement_confirmed' });
      setPlacementSentNow(true);
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to send email');
    } finally { setSendingPlacement(false); }
  };

  const handleCancelSession = async () => {
    if (!confirmCancel) { setConfirmCancel(true); return; }
    setBusy(true); setWError('');
    try {
      await api.delete(`/students/${student.id}/interview-session`);
      setConfirmCancel(false);
      setSession(null);
      onUpdate();
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to cancel session.');
    } finally { setBusy(false); }
  };

  const handleHostDeclined = async () => {
    if (!confirmDecline) { setConfirmDecline(true); return; }
    setBusy(true); setWError('');
    try {
      await api.post(`/students/${student.id}/host-declined`);
      setConfirmDecline(false);
      setMatchingHosts(null);
      setSelectedHostId('');
      onUpdate();
    } catch (err) {
      setWError(err.response?.data?.error || 'Failed to reset host assignment.');
    } finally { setBusy(false); }
  };

  const { hostKeys, studentKeys } = React.useMemo(() => {
    if (!session?.timezone) return { hostKeys: new Set(), studentKeys: new Set() };
    return {
      hostKeys: new Set((session.host_availability || []).map(iso => isoToSlotKey(iso, session.timezone)).filter(Boolean)),
      studentKeys: new Set((session.student_availability || []).map(iso => isoToSlotKey(iso, session.timezone)).filter(Boolean)),
    };
  }, [session]);

  const overlapCount = React.useMemo(() => {
    let n = 0;
    for (const k of hostKeys) { if (studentKeys.has(k)) n++; }
    return n;
  }, [hostKeys, studentKeys]);

  return (
    <>
      {showSetUpModal && (
        <SetUpInterviewModal
          student={student}
          onClose={() => { setShowSetUpModal(false); setWError(''); }}
          onSuccess={() => { setShowSetUpModal(false); onUpdate(); fetchSession(); }}
        />
      )}
      <div className="wf-card-glow-wrap">
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>Placement Workflow</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Host outreach and interview scheduling</div>
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
          {WORKFLOW_STEPS.map((step, i) => {
            const isActive = i === stepIndex;
            const isDone   = i < stepIndex;
            return (
              <div key={step.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div className={isActive ? 'wf-active-wrap' : ''} style={!isActive ? { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 } : { flex: 1 }}>
                  {isActive && <span className="wf-ripple-ring" />}
                  {isActive && <span className="wf-ripple-ring" />}
                  {isActive && <span className="wf-ripple-ring" />}
                  <div
                    className={isActive ? 'wf-active-dot' : ''}
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, position: 'relative', zIndex: 1,
                      background: isDone ? 'var(--navy)' : isActive ? 'var(--amber)' : 'var(--bg-sunken)',
                      color: isDone ? '#fff' : isActive ? 'var(--navy)' : 'var(--ink-4)',
                      border: isActive ? '2px solid var(--amber)' : '2px solid transparent',
                    }}
                  >
                    {isDone ? (
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,8 6,12 14,4" />
                      </svg>
                    ) : i + 1}
                  </div>
                  <div style={{ fontSize: 11, color: isActive ? 'var(--ink)' : 'var(--ink-4)', marginTop: 4, fontWeight: isActive ? 600 : 400, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    {step.label}
                  </div>
                </div>
                {i < WORKFLOW_STEPS.length - 1 && (
                  <div style={{ height: 2, width: 32, background: isDone ? 'var(--navy)' : 'var(--line)', marginBottom: 18, flexShrink: 0 }} />
                )}
              </div>
            );
          })}
        </div>

        {wError && (
          <div style={{ background: '#FEF2F2', color: '#B91C1C', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
            {wError}
          </div>
        )}

        {/* Booked confirmation banner */}
        {student.status === 'active' && bookedSession?.booked_slot && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)',
              border: '1.5px solid #6EE7B7',
              borderRadius: 10, padding: '14px 18px',
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#065F46', marginBottom: 2 }}>
                  Interview Confirmed
                </div>
                <div style={{ fontSize: 13, color: '#047857', fontWeight: 500 }}>
                  {fmtBookedSlotDisplay(bookedSession.booked_slot, bookedSession.timezone)}
                </div>
                {bookedSession.host_name && (
                  <div style={{ fontSize: 11.5, color: '#059669', marginTop: 3 }}>
                    with {bookedSession.host_name}
                  </div>
                )}
              </div>
            </div>

            {/* Step 4 — Send placement confirmed email */}
            <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--bg-sunken)', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', marginBottom: 4 }}>
                Next step: Send welcome email to {student.first_name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
                Congratulates the student on their confirmed placement and includes the weekly reflection sheets they need to fill out during practicum.
              </div>
              {placementAlreadySent ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#059669', fontWeight: 500 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Welcome email sent
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ marginLeft: 8, fontSize: 11 }}
                    disabled={sendingPlacement}
                    onClick={handleSendPlacementConfirmed}
                  >
                    {sendingPlacement ? 'Sending…' : 'Resend'}
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-primary"
                  disabled={sendingPlacement}
                  onClick={handleSendPlacementConfirmed}
                >
                  {sendingPlacement ? 'Sending…' : 'Send Welcome Email'}
                </button>
              )}
            </div>
          </>
        )}

        {/* Step 1 — Ready: Find Host */}
        {student.status === 'ready' && (
          <div>
            {!hasAllFiles ? (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#92400E' }}>
                <strong>Upload required before assigning a host.</strong>
                <div style={{ marginTop: 4, color: '#78350F' }}>
                  All three student files (Resume, Program Outline, Practicum Portfolio) must be uploaded before the outreach email can be sent.
                  Upload them in the <strong>Student Attachments</strong> section below.
                </div>
              </div>
            ) : matchingHosts === null ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="btn btn-primary" disabled={busy} onClick={handleFindHost}>
                  {busy ? 'Finding…' : 'Find Host'}
                </button>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  Filters by location, program, and executed agreement
                </span>
              </div>
            ) : matchingHosts.length === 0 ? (
              <div>
                <div style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 10 }}>
                  No matching hosts found in <strong>{student.campus || student.city || 'this area'}</strong> for <strong>{student.program_code}</strong> with an executed agreement.
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setMatchingHosts(null)}>Try Again</button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
                  {matchingHosts.length} matching host{matchingHosts.length !== 1 ? 's' : ''} found — select one and send outreach
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <select
                    className="select"
                    style={{ flex: 1, minWidth: 220 }}
                    value={selectedHostId}
                    onChange={e => setSelectedHostId(e.target.value)}
                  >
                    <option value="">Select a host…</option>
                    {matchingHosts.map(h => (
                      <option key={h.id} value={h.id}>
                        {h.org_name}{h.contact_first_name ? ` — ${h.contact_first_name} ${h.contact_last_name || ''}`.trim() : ''}
                      </option>
                    ))}
                  </select>
                  <button className="btn btn-primary" disabled={!selectedHostId || busy} onClick={handleAssignHost}>
                    {busy ? 'Sending…' : 'Assign & Send Outreach'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setMatchingHosts(null); setSelectedHostId(''); }}>
                    Cancel
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 8 }}>
                  This will send the host outreach email with {student.first_name}'s resume, program outline, and practicum portfolio attached.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Outreach Sent: Set Up Interview */}
        {student.status === 'host_pending_response' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '10px 14px', background: 'var(--bg-sunken)', borderRadius: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 2 }}>Outreach sent to</div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{student.host_name || 'Host'}</div>
              </div>
              {student.host_outreach_sent_at && (
                <div style={{ fontSize: 11, color: 'var(--ink-4)', textAlign: 'right' }}>
                  Sent {fmtDateTime(student.host_outreach_sent_at)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => { setShowSetUpModal(true); setWError(''); setConfirmDecline(false); }}>
                Schedule Interview
              </button>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                On the phone with the host? Enter the times they offered — only {student.first_name} gets a link to pick one.
              </span>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {confirmDecline ? (
                <>
                  <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                    This will unassign <strong>{student.host_name}</strong> and return the student to the host-finding step.
                  </span>
                  <button className="btn btn-danger btn-sm" disabled={busy} onClick={handleHostDeclined}>
                    {busy ? 'Resetting…' : 'Yes, host declined — reassign'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDecline(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="btn btn-ghost btn-sm btn-danger-ghost" onClick={handleHostDeclined}>
                    Host Declined
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                    Host turned down the student? Click to unassign and select a new host.
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 3 — Interview Scheduling (coordinator-driven) */}
        {student.status === 'interview_scheduling' && (
          <div>
            {sessionLoading ? (
              <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>Loading session…</div>
            ) : session ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '10px 14px', background: 'var(--bg-sunken)', borderRadius: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 2 }}>Interview with</div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{student.host_name || 'Host'}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', textAlign: 'right' }}>
                    {session.meeting_mode === 'online' ? '🎥 Online' : '📍 In person'} · {session.slot_minutes}min · {session.timezone}
                  </div>
                </div>

                {/* Meeting format / location */}
                <div style={{ fontSize: 12.5, color: 'var(--ink-2)', padding: '8px 12px', background: 'var(--bg-sunken)', borderRadius: 8, marginBottom: 14 }}>
                  {session.meeting_mode === 'online'
                    ? 'Online interview — a Google Meet link is generated automatically once the student picks a time.'
                    : <>In person at: <strong>{session.location || student.host_name}</strong></>}
                </div>

                {/* Waiting-on-student banner */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(91,160,229,.08)', border: '1px solid rgba(91,160,229,.22)', borderRadius: 8, marginBottom: 14 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5BA0E5', flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                    Waiting for <strong>{student.first_name}</strong> to pick a time. They've been emailed a link.
                  </div>
                </div>

                <div className="sched-links-block">
                  <SchedLink label="Student link" url={`${window.location.origin}/availability/${session.student_token}`} />
                </div>

                {/* Offered slots — PC can also book on the student's behalf */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>Offered times</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Student picks one — or book on their behalf</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(session.offered_slots || []).map((iso, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{fmtOffered(iso)}</span>
                        <button className="btn btn-ghost btn-sm" disabled={bookingBusy} onClick={() => handleBookOffered(iso)}>
                          {bookingBusy ? 'Booking…' : 'Book this'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setShowSetUpModal(true); setWError(''); }}>
                    Edit / Resend
                  </button>
                  {user?.role === 'admin' && (
                    confirmCancel ? (
                      <>
                        <button className="btn btn-danger btn-sm" disabled={busy} onClick={handleCancelSession}>
                          {busy ? 'Cancelling…' : 'Yes, Cancel & Reset'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmCancel(false)}>Never mind</button>
                      </>
                    ) : (
                      <button className="btn btn-ghost btn-sm btn-danger-ghost" onClick={handleCancelSession}>
                        Cancel & Reset
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
                No active session.{' '}
                <button className="btn btn-ghost btn-sm" onClick={() => setShowSetUpModal(true)}>Schedule Interview</button>
              </div>
            )}
          </div>
        )}
      </Card>
      </div>
    </>
  );
}

// ── Attachments Card ──────────────────────────────────────────────────────────

function AttachmentsCard({ student, onUpdate, user }) {
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRefs = useRef({});

  const handleUpload = async (type, file) => {
    setUploadingType(type);
    setUploadError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/students/${student.id}/upload/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingType(null);
      if (fileInputRefs.current[type]) fileInputRefs.current[type].value = '';
    }
  };

  const handleDelete = async (type) => {
    setUploadError('');
    try {
      await api.delete(`/students/${student.id}/upload/${type}`);
      onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleDownload = async (type) => {
    try {
      const res = await api.get(`/students/${student.id}/download/${type}`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const fileName = getDownloadFileName(cd, type);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { setUploadError('Download failed'); }
  };

  const allUploaded = ATTACHMENT_SLOTS.every(slot => getStudentAttachment(student, slot).stored);
  const uploadedCount = ATTACHMENT_SLOTS.filter(slot => getStudentAttachment(student, slot).stored).length;

  return (
    <section className="sdp-card" style={{ marginTop: 18 }}>
      <div className="card-head">
        <div>
          <span className="card-eyebrow">Documents</span>
          <h2 className="card-title">Student Attachments</h2>
        </div>
        <span className="card-foot-note">{uploadedCount} / {ATTACHMENT_SLOTS.length} uploaded</span>
      </div>

      {uploadError && (
        <div style={{ marginBottom: 14, padding: '10px 16px', background: '#FEF2F2', color: '#B91C1C', fontSize: 13, borderRadius: 10 }}>
          {uploadError}
        </div>
      )}

      {allUploaded && (
        <div className="attach-success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-12" /></svg>
          <span>All {ATTACHMENT_SLOTS.length} files uploaded — ready for host outreach.</span>
        </div>
      )}

      <div className="attach-list">
        {ATTACHMENT_SLOTS.map(slot => {
          const attachment = getStudentAttachment(student, slot);
          const filePath = attachment.stored;
          const fileName = attachment.fileName;
          const isUploading = uploadingType === slot.type;

          return (
            <div key={slot.type} className="attach">
              <span className="attach-icon pdf">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
              </span>
              <div className="attach-body">
                <div className="attach-name">{slot.label}</div>
                <div className="attach-meta">
                  {fileName ? fileName : <span style={{ fontStyle: 'italic', color: 'var(--ink-4)' }}>{attachment.pending ? 'Pending migration to database storage' : 'Not uploaded'}</span>}
                </div>
              </div>
              <div className="attach-actions">
                {filePath && (
                  <button className="attach-btn" onClick={() => handleDownload(slot.type)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                    Download
                  </button>
                )}
                {!student.is_archived && (
                  <label className="attach-upload-label" style={{ cursor: isUploading ? 'wait' : 'pointer' }}>
                    {isUploading ? 'Uploading…' : filePath ? (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
                        Replace
                      </>
                    ) : (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
                        Upload
                      </>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      disabled={!!uploadingType}
                      ref={el => { fileInputRefs.current[slot.type] = el; }}
                      onChange={e => e.target.files[0] && handleUpload(slot.type, e.target.files[0])}
                    />
                  </label>
                )}
                {filePath && user.role === 'admin' && !student.is_archived && (
                  <button className="attach-btn danger" onClick={() => handleDelete(slot.type)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetaCell({ label, value, highlight }) {
  return (
    <div className={`meta-cell${highlight ? ' meta-cell--highlight' : ''}`}>
      <div className="meta-label">{label}</div>
      <div className="meta-value">{value}</div>
    </div>
  );
}

function EField({ label, value, onChange, type = 'text', required = false }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="input" required={required} />
    </div>
  );
}
